import React from 'react';
import { useTranslation } from 'react-i18next';
import { UserProfile, View } from '../types';
import { getRoddoTheme } from '../constants/theme';
import {
  RoddoFishIcon,
  BriefcaseIcon,
  CameraIcon,
  CommunityIcon,
  MarketplaceIcon,
} from './icons';

interface BottomNavProps {
  activeView: View;
  navigateToRoot: (view: View, state?: Record<string, any>) => void;
  userProfile: UserProfile;
  navigateTo?: (view: View, state?: Record<string, any>) => void;
  isDarkMode: boolean;
  crewUnreadCount?: number;
}

type NavItem = {
  key: 'tacklebox' | 'log' | 'map' | 'community' | 'bazaar';
  view: View;
  Icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }> | null;
};

const BottomNav: React.FC<BottomNavProps> = ({ activeView, navigateToRoot, isDarkMode }) => {
  const { t } = useTranslation();
  const theme = getRoddoTheme(isDarkMode);

  const CORE_DOCK_H = 84; // must match --roddo-nav-h in src/index.css + App.tsx
  const CONTENT_H = 74;

  // Native iOS resolves this to 0px because Capacitor already owns the safe area.
  // Standalone browser/PWA can still provide a real bottom inset through CSS.
  const navSafePad = 'var(--roddo-nav-safe-pad, 0px)';
  const dockHeight = `calc(${CORE_DOCK_H}px + ${navSafePad})`;

  const navItems: NavItem[] = [
    { key: 'tacklebox', view: View.TackleBox, Icon: BriefcaseIcon },
    { key: 'log', view: View.LogCatch, Icon: CameraIcon },
    { key: 'map', view: View.Map, Icon: null },
    { key: 'community', view: View.Community, Icon: CommunityIcon },
    { key: 'bazaar', view: View.Bazaar, Icon: MarketplaceIcon },
  ];

  const navLabelOverride: Record<NavItem['key'], string> = {
    tacklebox: t('nav.tacklebox', 'Hold'),
    log: t('nav.log', 'Log'),
    map: t('nav.map', 'Big Blue'),
    community: t('nav.community', 'Dock'),
    bazaar: t('nav.bazaar', 'Bazaar'),
  };

  const inactiveIconColor = (key: NavItem['key']) => {
    if (key === 'map') {
      return isDarkMode ? '#7890C9' : '#4F6FAE';
    }

    return isDarkMode ? '#8B97AA' : '#71808C';
  };

  const activeIconColor = (key: NavItem['key']) => {
    switch (key) {
      case 'tacklebox':
        return isDarkMode ? '#66D9E8' : '#4F8CA8';
      case 'log':
        return isDarkMode ? '#66D6A9' : '#34D399';
      case 'map':
        return isDarkMode ? '#7F95FF' : '#245BFF';
      case 'community':
        return isDarkMode ? '#9C8CFF' : '#8B5CF6';
      case 'bazaar':
        return isDarkMode ? '#F2B84B' : '#D4A72C';
      default:
        return isDarkMode ? '#F4F6FA' : '#41515D';
    }
  };

  const activeFill = (key: NavItem['key']) => {
    if (isDarkMode) return 'rgba(138,112,242,0.10)';

    const alpha = 0.10;
    switch (key) {
      case 'tacklebox':
        return `rgba(132,223,246,${alpha})`;
      case 'log':
        return `rgba(82,228,181,${alpha})`;
      case 'community':
        return 'rgba(155,108,255,0.10)';
      case 'bazaar':
        return 'rgba(245,213,101,0.13)';
      case 'map':
        return 'rgba(95,101,255,0.09)';
      default:
        return 'transparent';
    }
  };

  const dockBackground = isDarkMode
    ? '#0A1120'
    : '#FFF9F1';

  const dockBorderTop = isDarkMode
    ? `1px solid ${theme.borderStrong}`
    : '1px solid rgba(116,86,54,0.14)';

  return (
    <nav
      data-roddo-bottom-nav="true"
      className="roddo-final-bottom-nav roddo-responsive-nav pointer-events-none relative w-full overflow-hidden rounded-b-[24px]"
      aria-label={t('nav.main', 'Main navigation')}
      style={{
        height: dockHeight,
        minHeight: dockHeight,
        maxHeight: dockHeight,
        background: dockBackground,
      }}
    >
      <div className="roddo-responsive-nav-frame pointer-events-auto relative h-full w-full">
        <div
          className="roddo-responsive-nav-surface absolute inset-x-0 bottom-0 rounded-b-[24px]"
          style={{
            height: dockHeight,
            minHeight: dockHeight,
            maxHeight: dockHeight,
            paddingBottom: navSafePad,
            background: dockBackground,
            borderTop: dockBorderTop,
            boxShadow: isDarkMode
              ? '0 -5px 16px rgba(0,0,0,0.18)'
              : '0 -7px 20px rgba(89,65,37,0.07)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            overflow: 'hidden',
          }}
        >
          <div
            className="pointer-events-none absolute inset-x-0 top-0 h-[2px]"
            style={{
              background: isDarkMode
                ? 'rgba(138,112,242,0.22)'
                : 'rgba(47,128,237,0.28)',
            }}
          />

          <div
            className="roddo-responsive-nav-content relative px-2.5 min-[390px]:px-3 sm:px-4"
            style={{ height: CONTENT_H, minHeight: CONTENT_H }}
          >
            <div className="roddo-responsive-nav-grid relative grid h-full grid-cols-5 items-center gap-1 min-[390px]:gap-1.5">
              {navItems.map((item) => {
                const active = item.view === activeView;
                const isMap = item.key === 'map';
                const label = navLabelOverride[item.key];
                const iconColor = active ? activeIconColor(item.key) : inactiveIconColor(item.key);
                const Icon = item.Icon;

                return (
                  <button
                    key={item.key}
                    type="button"
                    data-roddo-tour={`nav-${item.key}`}
                    onClick={() => {
                      if (item.view !== activeView) navigateToRoot(item.view);
                    }}
                    aria-label={label}
                    aria-current={active ? 'page' : undefined}
                    title={label}
                    className="roddo-responsive-nav-item roddo-tactile-hit group relative flex h-[64px] min-w-0 flex-col items-center justify-start rounded-[17px] px-1 pt-1 transition-transform duration-200 active:scale-95"
                    style={{
                      background: active ? activeFill(item.key) : 'transparent',
                      border: active
                        ? isDarkMode
                          ? '1px solid rgba(138,112,242,0.24)'
                          : '1px solid rgba(79,70,229,0.08)'
                        : '1px solid transparent',
                      boxShadow: active
                        ? isDarkMode
                          ? '0 4px 12px rgba(0,0,0,0.18)'
                          : '0 4px 10px rgba(89,65,37,0.05)'
                        : 'none',
                      color: iconColor,
                      transform: 'translateZ(0)',
                      WebkitTransform: 'translateZ(0)',
                      backfaceVisibility: 'hidden',
                      WebkitBackfaceVisibility: 'hidden',
                    }}
                  >
                    <span
                      className="relative z-10 grid place-items-center"
                      style={{
                        width: isMap ? (active ? 100 : 92) : active ? 36 : 33,
                        height: 38,
                        color: iconColor,
                        overflow: 'visible',
                      }}
                    >
                      {isMap ? (
                        <RoddoFishIcon
                          mode="png"
                          animated={false}
                          className="block object-contain object-center"
                          style={{
                            width: active ? 104 : 94,
                            height: active ? 56 : 50,
                            display: 'block',
                            maxWidth: 'none',
                            maxHeight: 'none',
                            objectFit: 'contain',
                            objectPosition: 'center',
                            transform: active
                              ? 'translateY(-5px)'
                              : 'translateY(-4px)',
                            transformOrigin: 'center',
                          }}
                        />
                      ) : Icon ? (
                        <Icon
                          className="block"
                          style={{
                            width: '100%',
                            height: '100%',
                            display: 'block',
                            color: iconColor,
                            fill: iconColor,
                            stroke: iconColor,
                            overflow: 'visible',
                            transform: 'translateZ(0)',
                            WebkitTransform: 'translateZ(0)',
                            backfaceVisibility: 'hidden',
                            WebkitBackfaceVisibility: 'hidden',
                          }}
                        />
                      ) : null}
                    </span>

                    <span
                      className="absolute inset-x-1 bottom-[5px] z-10 block h-[13px] truncate text-center text-[10px] font-black leading-[13px] tracking-[-0.01em]"
                      style={{
                        color: active
                          ? isDarkMode
                            ? '#F4F6FA'
                            : '#233746'
                          : isDarkMode
                            ? '#98A3B4'
                            : '#65727C',
                        textShadow: isDarkMode
                          ? '0 1px 2px rgba(0,0,0,0.30)'
                          : 'none',
                      }}
                    >
                      {label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default BottomNav;